// CUSTOM: personalised-storage-manager.js
// Centralized storage management for the personalised bundle system

(function() {
  'use strict';
  
  // Safety check - ensure namespace exists
  if (typeof window.personalised === 'undefined') {
    console.warn('[StorageManager] window.personalised namespace not found. StorageManager initialization may fail.');
    window.personalised = {};
  }
  
  // Don't re-register if already defined (idempotent initialization)
  if (window.personalised.storageManager) {
    console.info('[StorageManager] Already initialized. Skipping registration.');
    return;
  }
  
  /**
   * @typedef {Object} BundleState
   * @property {string} frequency - Subscription frequency ("30", "60", or "90")
   * @property {number} multiplier - Frequency multiplier (1, 2, or 3)
   * @property {Array<Object>} items - Bundle items
   * @property {Object} [bundleMetadata] - Optional metadata for Recharge
   * @property {string} [bundleMetadata.bundleSelectionId] - Recharge bundle selection ID
   * @property {string} [bundleMetadata.subscriptionId] - Recharge subscription ID
   * @property {string} [bundleMetadata.purchaseItemId] - Recharge purchase item ID
   */
  
  /**
   * @typedef {Object} ModeOptions
   * @property {string} [source] - Source of the mode change for debugging
   * @property {string|null} [subscriptionId] - Associated subscription ID if in edit mode
   */
  
  /**
   * @typedef {Object} MetadataSchema
   * @property {string} [bundleSelectionId] - Recharge bundle selection ID
   * @property {string} [subscriptionId] - Recharge subscription ID for current bundle
   * @property {string} [purchaseItemId] - Recharge purchase item ID
   * @property {string} [originalFrequency] - Original frequency from Recharge
   * @property {string} [externalProductId] - External product ID (Shopify) for the bundle
   * @property {string} [externalVariantId] - External variant ID (Shopify) for the bundle
   * @property {string} [bundleRechargeId] - Recharge internal bundle ID
   * @property {boolean} [loadedFromRecharge] - Flag indicating if bundle was loaded from Recharge
   * @property {Object} [subscriptionDetails] - Subscription details object with essential data
   * @property {string} [subscriptionDetails.id] - Subscription ID
   * @property {string} [subscriptionDetails.status] - Subscription status
   * @property {string} [subscriptionDetails.order_interval_frequency] - Order frequency
   * @property {string} [subscriptionDetails.purchaseItemId] - Purchase item ID
   * @property {string} [subscriptionDetails.bundleSelectionId] - Bundle selection ID
   * @property {number} [subscriptionDetails.lastFetched] - Timestamp of last fetch
   */
  
  /**
   * StorageManager - Centralized handler for all personalised bundle storage operations
   * 
   * This module provides a single source of truth for:
   * - Bundle state persistence (localStorage)
   * - Session mode tracking (sessionStorage) 
   * - Bundle metadata synchronization
   * - State modification tracking
   * 
   * IMPORTANT: Direct access to storage is deprecated. Always use these methods.
   */
  const storageManager = {
    // Constants for storage keys
    KEYS: {
      BUNDLE_STATE: 'personalised_bundle_state',
      BUNDLE_MODIFIED: 'personalised_bundle_state_modified',
      MODE: 'personalised_mode',
      METADATA: 'personalised_metadata'
    },
    
    /**
     * Gets the current bundle state from localStorage
     * Returns null if no state exists or if there's a parsing error
     * @returns {BundleState|null} The bundle state or null if not available
     */
    getBundleState: function() {
      try {
        const savedData = localStorage.getItem(this.KEYS.BUNDLE_STATE);
        if (!savedData) {
          return null;
        }
        
        const state = JSON.parse(savedData);
        
        // Basic validation to ensure we have the minimal required structure
        if (!state || typeof state.frequency !== 'string' || !Array.isArray(state.items)) {
          console.warn('[StorageManager] Invalid bundle state structure in localStorage');
          return null;
        }
        
        return state;
      } catch (err) {
        console.error('[StorageManager] Failed to parse bundle state from localStorage:', err);
        return null;
      }
    },
    
    /**
     * Saves the bundle state to localStorage
     * @param {BundleState} state - The bundle state to save
     * @returns {boolean} True if saved successfully, false otherwise
     */
    saveBundleState: function(state) {
      if (!state || !state.frequency || !Array.isArray(state.items)) {
        console.error('[StorageManager] Invalid state object provided to saveBundleState');
        return false;
      }
      
      try {
        const stateString = JSON.stringify(state);
        localStorage.setItem(this.KEYS.BUNDLE_STATE, stateString);
        return true;
      } catch (err) {
        console.error('[StorageManager] Failed to save bundle state to localStorage:', err);
        return false;
      }
    },
    
    /**
     * Clears the bundle state from localStorage
     * @returns {boolean} True if cleared successfully, false otherwise
     */
    clearBundleState: function() {
      try {
        localStorage.removeItem(this.KEYS.BUNDLE_STATE);
        return true;
      } catch (err) {
        console.error('[StorageManager] Failed to clear bundle state from localStorage:', err);
        return false;
      }
    },
    
    /**
     * Gets the current mode from sessionStorage
     * @returns {string} The current mode ('edit', 'new', or 'guest')
     */
    getMode: function() {
      try {
        const stored = JSON.parse(sessionStorage.getItem(this.KEYS.MODE) || '{}');
        
        const validModes = ['edit', 'new', 'guest'];
        if (validModes.includes(stored.mode)) {
          return stored.mode;
        }
        
        // Default fallback if no valid mode is found
        return 'guest';
      } catch (e) {
        console.warn('[StorageManager] Failed to parse mode from sessionStorage', e);
        return 'guest';
      }
    },
    
    /**
     * Sets the current mode and persists it to sessionStorage
     * @param {string} mode - The mode to set ('edit', 'new', or 'guest')
     * @param {ModeOptions} [options={}] - Additional options
     * @returns {boolean} True if set successfully, false otherwise
     */
    setMode: function(mode, options = {}) {
      try {
        const normalizedMode = String(mode).toLowerCase();
        const validModes = ['edit', 'new', 'guest'];
        
        if (!validModes.includes(normalizedMode)) {
          console.warn(`[StorageManager] Invalid mode '${mode}' – defaulting to guest`);
          mode = 'guest';
        }
        
        const source = options.source || 'unknown';
        const subscriptionId = options.subscriptionId || null;
        
        const data = {
          mode: normalizedMode,
          timestamp: Date.now(),
          source,
          ...(subscriptionId && { subscriptionId })
        };
        
        sessionStorage.setItem(this.KEYS.MODE, JSON.stringify(data));
        return true;
      } catch (err) {
        console.error('[StorageManager] Failed to set mode in sessionStorage:', err);
        return false;
      }
    },
    
    /**
     * Gets the subscription ID associated with the current mode (if any)
     * @returns {string|null} The subscription ID or null if not available
     * 
     * Retrieves the subscription ID from the current mode object in sessionStorage.
     * Preferred over direct parsing to ensure format consistency and safer fallbacks.
     * Centralizes access to subscription data and prevents JSON parsing errors.
     */
    getModeSubscriptionId: function() {
      try {
        const stored = JSON.parse(sessionStorage.getItem(this.KEYS.MODE) || '{}');
        return stored.subscriptionId || null;
      } catch (e) {
        console.warn('[StorageManager] Failed to read subscriptionId from sessionStorage', e);
        return null;
      }
    },
    
    /**
     * Checks if mode data exists in sessionStorage
     * @returns {boolean} True if mode data exists, false otherwise
     */
    hasStoredMode: function() {
      return !!sessionStorage.getItem(this.KEYS.MODE);
    },
    
    /**
     * Marks the bundle state as modified in the current session
     * This prevents Recharge data from overwriting user changes when navigating between pages
     * @returns {boolean} True if flag was set successfully, false otherwise
     */
    markBundleModified: function() {
      try {
        sessionStorage.setItem(this.KEYS.BUNDLE_MODIFIED, 'true');
        return true;
      } catch (err) {
        console.error('[StorageManager] Failed to set modified flag in sessionStorage:', err);
        return false;
      }
    },
    
    /**
     * Checks if the bundle state has been modified in the current session
     * @returns {boolean} True if the bundle has been modified, false otherwise
     */
    isBundleModified: function() {
      return sessionStorage.getItem(this.KEYS.BUNDLE_MODIFIED) === 'true';
    },
    
    /**
     * Clears the bundle modified flag from sessionStorage
     * @returns {boolean} True if cleared successfully, false otherwise
     */
    clearModifiedFlag: function() {
      try {
        sessionStorage.removeItem(this.KEYS.BUNDLE_MODIFIED);
        return true;
      } catch (err) {
        console.error('[StorageManager] Failed to clear modified flag from sessionStorage:', err);
        return false;
      }
    },
    
    /**
     * Gets a specific metadata value by key from localStorage
     * @param {string} key - The metadata key to retrieve
     * @param {*} [defaultValue=null] - Default value to return if key not found
     * @returns {*} The metadata value or defaultValue if not found
     */
    getMetadata: function(key, defaultValue = null) {
      if (!key || typeof key !== 'string') {
        console.warn('[StorageManager] Invalid key provided to getMetadata');
        return defaultValue;
      }
      
      try {
        const metadataString = localStorage.getItem(this.KEYS.METADATA);
        if (!metadataString) {
          return defaultValue;
        }
        
        const metadata = JSON.parse(metadataString);
        return metadata.hasOwnProperty(key) ? metadata[key] : defaultValue;
      } catch (err) {
        console.error('[StorageManager] Failed to get metadata from localStorage:', err);
        return defaultValue;
      }
    },
    
    /**
     * Normalizes a metadata value according to the expected schema type for a specific key
     * Used to ensure data type consistency before storage
     * 
     * @param {string} key - The metadata key
     * @param {*} value - The value to normalize
     * @returns {*} - The normalized value according to schema type
     * @private
     */
    normalizeMetadataForSchema: function(key, value) {
      // Return null for null or undefined values
      if (value === null || value === undefined) return null;
      
      // For ID fields and frequencies, convert to string
      switch (key) {
        case 'bundleSelectionId':
        case 'subscriptionId':
        case 'purchaseItemId':
        case 'originalFrequency':
        case 'externalProductId':
        case 'externalVariantId':
        case 'bundleRechargeId':
          return String(value);
        
        // For other fields, return as-is (validation will handle type checking)
        default:
          return value;
      }
    },
    
    /**
     * Sets a specific metadata value by key in localStorage
     * @param {string} key - The metadata key to set
     * @param {*} value - The value to store (must be JSON serializable)
     * @returns {boolean} True if saved successfully, false otherwise
     */
    setMetadata: function(key, value) {
      if (!key || typeof key !== 'string') {
        console.error('[StorageManager] Invalid key provided to setMetadata');
        return false;
      }
      
      // Normalize the value according to schema type before validation and storage
      const normalizedValue = this.normalizeMetadataForSchema(key, value);
      
      // Optional validation - will log warnings but still save the data
      this.validateMetadata(key, normalizedValue);
      
      try {
        // Get current metadata or initialize new object
        let metadata = {};
        const metadataString = localStorage.getItem(this.KEYS.METADATA);
        
        if (metadataString) {
          try {
            metadata = JSON.parse(metadataString);
          } catch (parseErr) {
            console.warn('[StorageManager] Failed to parse existing metadata, creating new object');
            metadata = {};
          }
        }
        
        // Update the metadata with normalized value
        metadata[key] = normalizedValue;
        
        // Save back to localStorage
        localStorage.setItem(this.KEYS.METADATA, JSON.stringify(metadata));
        return true;
      } catch (err) {
        console.error('[StorageManager] Failed to save metadata to localStorage:', err);
        return false;
      }
    },
    
    /**
     * Clears a specific metadata key from localStorage
     * @param {string} key - The metadata key to clear
     * @returns {boolean} True if cleared successfully, false otherwise
     */
    clearMetadata: function(key) {
      if (!key || typeof key !== 'string') {
        console.error('[StorageManager] Invalid key provided to clearMetadata');
        return false;
      }
      
      try {
        const metadataString = localStorage.getItem(this.KEYS.METADATA);
        if (!metadataString) {
          return true; // Nothing to clear
        }
        
        let metadata = JSON.parse(metadataString);
        if (metadata.hasOwnProperty(key)) {
          delete metadata[key];
          localStorage.setItem(this.KEYS.METADATA, JSON.stringify(metadata));
        }
        return true;
      } catch (err) {
        console.error('[StorageManager] Failed to clear metadata key from localStorage:', err);
        return false;
      }
    },
    
    /**
     * Clears all metadata from localStorage
     * @returns {boolean} True if cleared successfully, false otherwise
     */
    clearAllMetadata: function() {
      try {
        localStorage.removeItem(this.KEYS.METADATA);
        return true;
      } catch (err) {
        console.error('[StorageManager] Failed to clear all metadata from localStorage:', err);
        return false;
      }
    },
    
    /**
     * Clears ALL personalized bundle data from both localStorage and sessionStorage
     * This provides a comprehensive cleanup for user logout scenarios
     * @returns {boolean} True if cleared successfully, false otherwise
     */
    clearAllPersonalisedData: function() {
      try {
        // Clear localStorage
        localStorage.removeItem(this.KEYS.BUNDLE_STATE);
        localStorage.removeItem(this.KEYS.METADATA);
        localStorage.removeItem('personalised_user_logged_in'); // Clear login tracking key
        localStorage.removeItem('vitable_pregnancy'); // Clear quiz pregnancy data
        localStorage.removeItem('vitable_allergy'); // Clear quiz allergy data
        localStorage.removeItem('vitable_name_on_pack'); // Clear quiz name_on_pack data
        localStorage.removeItem('vitable_latest_quiz_date'); // Clear quiz date timestamp
        localStorage.removeItem('vitable.recommendation'); // Clear recommendation data
        
        // Clear sessionStorage
        sessionStorage.removeItem(this.KEYS.BUNDLE_MODIFIED);
        sessionStorage.removeItem(this.KEYS.MODE);
        sessionStorage.removeItem('personalised_customer_id');
        
        // Set session freshness flag when clearing data
        sessionStorage.setItem('personalised_session_fresh', 'true');
        
        console.log('[Storage] All personalized bundle data cleared');
        return true;
      } catch (err) {
        console.error('[Storage] Failed to clear all personalized data:', err);
        return false;
      }
    },
    
    /**
     * Gets the bundle metadata from localStorage
     * @deprecated Use getMetadata instead for specific keys
     * @returns {Object|null} The bundle metadata or null if not available
     */
    getBundleMetadata: function() {
      try {
        const metadataString = localStorage.getItem(this.KEYS.METADATA);
        if (!metadataString) {
          return null;
        }
        
        return JSON.parse(metadataString);
      } catch (err) {
        console.error('[StorageManager] Failed to get bundle metadata from localStorage:', err);
        return null;
      }
    },
    
    /**
     * Saves the bundle metadata to localStorage
     * @deprecated Use setMetadata instead for specific keys
     * @param {Object} metadata - The metadata to save
     * @returns {boolean} True if saved successfully, false otherwise
     */
    saveBundleMetadata: function(metadata) {
      if (!metadata || typeof metadata !== 'object') {
        console.error('[StorageManager] Invalid metadata object provided to saveBundleMetadata');
        return false;
      }
      
      try {
        localStorage.setItem(this.KEYS.METADATA, JSON.stringify(metadata));
        return true;
      } catch (err) {
        console.error('[StorageManager] Failed to save bundle metadata to localStorage:', err);
        return false;
      }
    },
    
    /**
     * Validates if a metadata value conforms to the expected schema for a specific key
     * This is a soft validation that logs warnings but doesn't prevent saving invalid data
     * 
     * @param {string} key - The metadata key to validate
     * @param {*} value - The value to validate
     * @returns {boolean} True if valid, false otherwise
     */
    validateMetadata: function(key, value) {
      // Key must be a string
      if (typeof key !== 'string') {
        console.warn('[StorageManager] Invalid metadata key type:', typeof key);
        return false;
      }
      
      // Key-specific validation
      switch (key) {
        case 'bundleSelectionId':
        case 'subscriptionId':
        case 'purchaseItemId':
        case 'originalFrequency':
        case 'externalProductId':
        case 'externalVariantId':
        case 'bundleRechargeId':
          // These should be strings or null
          if (value !== null && typeof value !== 'string') {
            console.warn(`[StorageManager] Expected string or null for ${key}, got:`, typeof value);
            return false;
          }
          break;
          
        case 'loadedFromRecharge':
          // Should be a boolean
          if (typeof value !== 'boolean') {
            console.warn(`[StorageManager] Expected boolean for ${key}, got:`, typeof value);
            return false;
          }
          break;
          
        case 'subscriptionDetails':
          // Should be an object with specific properties
          if (!value || typeof value !== 'object') {
            console.warn(`[StorageManager] Expected object for ${key}, got:`, typeof value);
            return false;
          }
          
          // Check essential properties
          const requiredProps = ['id', 'lastFetched'];
          const missingProps = requiredProps.filter(prop => !value.hasOwnProperty(prop));
          
          if (missingProps.length > 0) {
            console.warn(`[StorageManager] Missing required properties in ${key}:`, missingProps);
            return false;
          }
          
          // Validate id is a string and lastFetched is a number
          if (typeof value.id !== 'string') {
            console.warn(`[StorageManager] Expected string for ${key}.id, got:`, typeof value.id);
            return false;
          }
          
          if (typeof value.lastFetched !== 'number') {
            console.warn(`[StorageManager] Expected number for ${key}.lastFetched, got:`, typeof value.lastFetched);
            return false;
          }
          break;
          
        default:
          // For unknown keys, just log a warning but consider them valid
          console.info(`[StorageManager] Unknown metadata key: ${key}`);
          return true;
      }
      
      return true;
    },
    
    /**
     * Gets the metadata schema from localStorage
     * @returns {MetadataSchema|null} The metadata schema or null if not available
     */
    getMetadataSchema: function() {
      try {
        const schemaString = localStorage.getItem(this.KEYS.METADATA);
        if (!schemaString) {
          return null;
        }
        
        return JSON.parse(schemaString);
      } catch (err) {
        console.error('[StorageManager] Failed to get metadata schema from localStorage:', err);
        return null;
      }
    },
    
    /**
     * Sets the metadata schema in localStorage
     * @param {MetadataSchema} schema - The metadata schema to set
     * @returns {boolean} True if set successfully, false otherwise
     */
    setMetadataSchema: function(schema) {
      if (!schema || typeof schema !== 'object') {
        console.error('[StorageManager] Invalid schema object provided to setMetadataSchema');
        return false;
      }
      
      try {
        localStorage.setItem(this.KEYS.METADATA, JSON.stringify(schema));
        return true;
      } catch (err) {
        console.error('[StorageManager] Failed to save metadata schema to localStorage:', err);
        return false;
      }
    }
  };
  
  // Register the storageManager in the personalised namespace
  window.personalised.storageManager = storageManager;
  
  console.info('[StorageManager] Successfully initialized with metadata support');
})();
